import { catchError, delay, first, map, tap, filter } from "rxjs/operators";
import { StoreModel } from "./../models/store.model";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { environment } from "src/environments/environment";
import { firstPropertyImageFileName } from "./property-image.util";
import { CookieService } from "ngx-cookie-service";
import { PropertyDataService } from "src/app/core/services/property-data.service";
import { DomSanitizer } from "@angular/platform-browser";
import { AuthService } from "../../../core/services/auth.service";

@Injectable({
  providedIn: "root",
})
export class PropertiesService {
  storeAgent = 0;
  constructor(
    private http: HttpClient,
    public _cookiesService: CookieService,
    private propertyDataService: PropertyDataService,
    private sanitizer: DomSanitizer,
    private authService: AuthService,
  ) {}

  getUserAuth() {
    return this.authService.currentUserValue;
  }

  canCreateProperties() {
    return this.http
      .get<{
        available: boolean;
        quantity: number;
      }>(`${environment.apiUrl}/subscriptions-plans/properties`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /**
   * Get all properties
   * @param page
   * @param search
   * @returns
   */
  getAll1(page: number, search = ""): Observable<any> {
    const lang = this._cookiesService.get("lang");
    let params = new HttpParams().set("page", page);
    params = params.set("dis", [1, 2, 3].join(","));
    if (search !== "") params = params.set("search", search);
    return this.http
      .get<any>(`${environment.apiUrl}/properties/all/${lang}`, {
        params,
        withCredentials: true,
      })
      .pipe(
        first(),
        map((data) => {
          return {
            properties:
              data.properties.map((p: any) => {
                return {
                  id: p.id,
                  title: p?.propertyTranslations[0]?.title,
                  mediation: p.endMediationAgreement,
                  reference: p.reference,
                  propertyType: {
                    name: p.propertyType.propertyTypeTranslations[0]
                      .description,
                    type: p?.propertyType?.type,
                  },
                  propertyType2: {
                    name: p?.propertyType2?.propertyTypeTranslations[0]
                      .description,
                    type: p?.propertyType2?.type,
                  },
                  businessType: {
                    name: p.propertyBusinessType
                      .propertyBusinessTypeTranslations[0].description,
                  },
                  businessStatus: {
                    name: p?.propertyBusinessStatus
                      ?.propertyBusinessStatusTranslations?.[0]?.description,
                    id: p?.propertyBusinessStatus?.id,
                    code: p?.propertyBusinessStatus?.code,
                  },
                  price: p.price,
                  rentalPrice: p.rentalPrice,
                  store: p.store,
                  updated: p.updatedAt,
                  createdAt: p.createdAt,
                  validatedAt: p.validatedAt,
                  exclusive: p.exclusive === true,
                  isResale: p?.isResale === true,
                  resaleOf: p?.resaleOf ?? null,
                  image: firstPropertyImageFileName(p.propertyFiles),
                  associatedProperty: p?.associatedProperty,
                  status: p.propertySettingsStatus.length > 0,
                };
              }) || [],
            total: data.total,
          };
        }),
        catchError((error) => {
          return of({ properties: [], total: 0 });
        }),
      );
  }

  hasOutdatedParishProperties(): Observable<{ exists: boolean }> {
    return this.http
      .get<{ exists: boolean }>(
        `${environment.apiUrl}/properties/outdated-parish/exists`,
        { withCredentials: true },
      )
      .pipe(first());
  }

  getAll(data: any): Observable<any> {
    const lang = this._cookiesService.get("lang");

    return this.http
      .post<any>(`${environment.apiUrl}/properties/all/${lang}`, data, {
        withCredentials: true,
      })
      .pipe(
        first(),
        map((data) => {
          return {
            properties:
              data.properties.map((p: any) => {
                return {
                  id: p.id,
                  title: p?.propertyTranslations[0]?.title,
                  mediation: p.endMediationAgreement,
                  reference: p.reference,
                  propertyType: {
                    name: p?.propertyType?.propertyTypeTranslations[0]
                      ?.description,
                    type: p?.propertyType?.type,
                  },
                  propertyType2: {
                    name: p?.propertyType2?.propertyTypeTranslations[0]
                      ?.description,
                    type: p?.propertyType2?.type,
                  },
                  businessType: {
                    name: p?.propertyBusinessType
                      ?.propertyBusinessTypeTranslations[0]?.description,
                    id: p?.propertyBusinessType?.id,
                  },
                  businessStatus: {
                    name: p?.propertyBusinessStatus
                      ?.propertyBusinessStatusTranslations[0]?.description,
                    id: p?.propertyBusinessStatus?.id,
                    code: p?.propertyBusinessStatus?.code,
                  },
                  price: p.price,
                  rentalPrice: p.rentalPrice,
                  store: p.store,
                  updated: p.updatedAt,
                  createdAt: p.createdAt,
                  validatedAt: p.validatedAt,
                  exclusive: p.exclusive === true,
                  isResale: p?.isResale === true,
                  resaleOf: p?.resaleOf ?? null,
                  image:
                    p.propertyFiles?.filter(
                      (im: any) => im.fieldName === "images",
                    )?.length > 0
                      ? p.propertyFiles?.filter(
                          (im: any) => im.fieldName === "images",
                        )[0]?.fileName
                      : "",
                  associatedProperty: p?.associatedProperty
                    ? {
                        ...p?.associatedProperty,
                        _count: undefined,
                        units:
                          p?.associatedProperty?._count?.associatedProperties,
                      }
                    : undefined,
                  status:
                    p?.propertySettingsStatus?.length > 0 &&
                    p?.propertySettingsStatus?.find(
                      (ps: any) =>
                        ps?.propertySettings?.description === "Active",
                    )
                      ? true
                      : false,
                  mainAgent:
                    p?.propertyAgents?.length > 0
                      ? p.propertyAgents[0]?.userId
                      : undefined,
                  portalLogs: p?.portalsLogs?.length > 0 ? true : false,

                  highlight: p?._count?.propertySettingsStatus > 0,
                  currency: p?.currency?.symbol,
                  saleInProgress: p?.sale?.length > 0 ? true : false,
                  isProcessing:
                    p?.propertySettingsStatus?.length > 0 &&
                    p?.propertySettingsStatus?.find(
                      (ps: any) =>
                        ps?.propertySettings?.description === "Processing",
                    )
                      ? true
                      : false,
                  agent:
                    p?.propertyAgents?.length > 0
                      ? {
                          name: p?.propertyAgents?.[0]?.user?.name,
                          image:
                            p?.propertyAgents?.[0]?.user?.userFiles?.length > 0
                              ? p?.propertyAgents?.[0]?.user?.userFiles?.[0]
                                  ?.fileName
                              : undefined,
                        }
                      : undefined,
                  contractNumber: p?.contractNumber,
                  zone: p?.propertyAddress?.[0]?.zone,
                  preview: {
                    reference: p?.reference,
                    zone: p?.propertyAddress?.[0]?.zone,
                    propertyType:
                      p?.propertyType2?.propertyTypeTranslations[0]
                        ?.description ??
                      p?.propertyType?.propertyTypeTranslations[0]?.description,
                    businessType:
                      p?.propertyBusinessType
                        ?.propertyBusinessTypeTranslations[0]?.description,
                    businessStatus: {
                      name: p?.propertyBusinessStatus
                        ?.propertyBusinessStatusTranslations[0]?.description,
                      id: p?.propertyBusinessStatus?.id,
                      code: p?.propertyBusinessStatus?.code,
                    },
                    condition:
                      p?.propertyCondition?.propertyConditionTranslations[0]
                        ?.description,
                    exclusive: p?.exclusive,
                    isResale: p?.isResale === true,
                    resaleOf: p?.resaleOf ?? null,
                    price: p?.price,
                    propertyId: p.id,
                    appId: p.appId,
                    entityId: p.entityId,
                    title: p?.propertyTranslations[0]?.title,
                    description: p?.propertyTranslations[0]?.description,
                    images: p.propertyFiles
                      ?.filter((im: any) => im.fieldName === "images")
                      ?.map((f: any) => ({
                        url: f?.fileName,
                        description: f?.description,
                        fileId: f?.id,
                      })),
                    yearOfConstruction: p?.yearOfConstruction,
                    bedrooms:
                      p?.propertyDivisions?.find(
                        (d: any) => d?.division?.description === "Bedrooms",
                      )?.val ?? 0,
                    bathrooms:
                      p?.propertyDivisions?.find(
                        (d: any) => d?.division?.description === "Bathrooms",
                      )?.val ?? 0,
                    createdAt: p?.createdAt,
                    ec: p?.energeticCertification
                      ?.energeticCertificationTranslations[0]?.description,
                    grossArea: p?.grossArea,
                    usefulArea: p?.usefulArea,
                    owner: p?.contactsCostumersProperties?.map((c: any) => ({
                      name: c?.contact?.firstName + " " + c?.contact?.lastName,
                      email: c?.contact?.email,
                      phone: c?.contact?.phone,
                      fiscalIdentification: c?.contact?.fiscalIdentification,
                      contactType: c?.contact?.contactType,
                    }))[0],
                    district: p?.propertyAddress[0]?.district?.name,
                    county: p?.propertyAddress[0]?.county?.name,
                    parish: p?.propertyAddress[0]?.parish?.name,
                    portals: p?.entity?.entityPortals
                      ?.map((e: any) => ({
                        name: e?.portal?.name,
                        status: p?.propertyPortals?.find(
                          (p: any) => p?.portalId === e?.portalId,
                        )
                          ? true
                          : false,
                      }))
                      .sort((p1: any, p2: any) => p2?.status - p1?.status),
                    documents: p.propertyFiles
                      ?.filter((im: any) => im.fieldName === "docs")
                      ?.map((f: any) => ({
                        url: f?.fileName,
                        description: f?.description,
                        fileId: f?.id,
                      })),
                    agent:
                      p?.propertyAgents?.length > 0
                        ? {
                            name: p?.propertyAgents?.[0]?.user?.name,
                            phone:
                              p?.propertyAgents?.[0]?.user?.userAddress
                                ?.contact1,
                            email: p?.propertyAgents?.[0]?.user?.email,
                            image:
                              p?.propertyAgents?.[0]?.user?.userFiles?.length >
                              0
                                ? p?.propertyAgents?.[0]?.user?.userFiles?.[0]
                                    ?.fileName
                                : undefined,
                          }
                        : undefined,
                    store: p.store,

                    // proposals?: any[];
                    // oportunities?: any[];
                    // logs?: any[];
                  },
                };
              }) || [],
            total: data.total,
            maxPrice: data.maxPrice,
            totalExported: data?.totalExported,
          };
        }),
        catchError((error) => {
          console.log(error);
          return of({ properties: [], total: 0 });
        }),
      );
  }

  findPrevById(id: any) {
    const lang = this._cookiesService.get("lang");

    return this.http
      .get<any>(`${environment.apiUrl}/properties/by/${id}/${lang}`, {
        withCredentials: true,
      })
      .pipe(
        first(),
        map((p) => {
          return {
            id: p.id,
            title: p?.propertyTranslations[0]?.title,
            mediation: p.endMediationAgreement,
            reference: p.reference,
            propertyType: {
              name: p.propertyType.propertyTypeTranslations[0].description,
              type: p?.propertyType?.type,
            },
            propertyType2: {
              name: p?.propertyType2?.propertyTypeTranslations[0].description,
              type: p?.propertyType2?.type,
            },
            businessType: {
              name: p.propertyBusinessType?.propertyBusinessTypeTranslations[0]
                ?.description,
            },
            businessStatus: {
              name: p.propertyBusinessStatus
                ?.propertyBusinessStatusTranslations[0]?.description,
              id: p?.propertyBusinessStatus?.id,
              code: p?.propertyBusinessStatus?.code,
            },
            price: p.price,
            store: p.store,
            updated: p.updatedAt,
            createdAt: p.createdAt,
            validatedAt: p.validatedAt,
            image: firstPropertyImageFileName(p?.propertyFiles),
            associatedProperty: p?.associatedProperty,
            isResale: p?.isResale === true,
            resaleOf: p?.resaleOf ?? null,
            status: p?.propertySettingsStatus?.length > 0,
            mainAgent:
              p?.propertyAgents?.length > 0
                ? p.propertyAgents[0]?.userId
                : undefined,
            portalLogs: p?.portalsLogs?.length > 0 ? true : false,
            zone: p?.propertyAddress?.[0]?.zone,
            preview: {
              zone: p?.propertyAddress?.[0]?.zone,
              propertyType:
                p?.propertyType2?.propertyTypeTranslations[0]?.description ??
                p?.propertyType?.propertyTypeTranslations[0]?.description,
              businessType:
                p?.propertyBusinessType?.propertyBusinessTypeTranslations[0]
                  ?.description,
              businessStatus: {
                name: p?.propertyBusinessStatus
                  ?.propertyBusinessStatusTranslations[0]?.description,
                id: p?.propertyBusinessStatus?.id,
                code: p?.propertyBusinessStatus?.code,
              },
              condition:
                p?.propertyCondition?.propertyConditionTranslations[0]
                  ?.description,
              exclusive: p?.exclusive,
              isResale: p?.isResale === true,
              resaleOf: p?.resaleOf ?? null,
              price: p?.price,
              title: p?.propertyTranslations[0]?.title,
              description: p?.propertyTranslations[0]?.description,
              images: p.propertyFiles
                ?.filter((im: any) => im.fieldName === "images")
                ?.map((f: any) => ({
                  url: f?.fileName,
                  description: f?.description,
                  documentVisibility: f?.documentVisibility,
                  showOnWebsite: f?.showOnWebsite,
                })),
              yearOfConstruction: p?.yearOfConstruction,
              bedrooms:
                p?.propertyDivisions?.find(
                  (d: any) => d?.division?.description === "Bedrooms",
                )?.val ?? 0,
              bathrooms:
                p?.propertyDivisions?.find(
                  (d: any) => d?.division?.description === "Bathrooms",
                )?.val ?? 0,
              createdAt: p?.createdAt,
              ec: p?.energeticCertification
                ?.energeticCertificationTranslations[0]?.description,
              grossArea: p?.grossArea,
              usefulArea: p?.usefulArea,
              owner: p?.contactsCostumersProperties?.map((c: any) => ({
                name: c?.contact?.firstName + " " + c?.contact?.lastName,
                email: c?.contact?.email,
                phone: c?.contact?.phone,
              }))[0],
              reference: p?.reference,
              district: p?.propertyAddress[0]?.district?.name,
              county: p?.propertyAddress[0]?.county?.name,
              parish: p?.propertyAddress[0]?.parish?.name,
              portals: p?.entity?.entityPortals
                ?.map((e: any) => ({
                  name: e?.portal?.name,
                  status: p?.propertyPortals?.find(
                    (p: any) => p?.portalId === e?.portalId,
                  )
                    ? true
                    : false,
                }))
                .sort((p1: any, p2: any) => p2?.status - p1?.status),
              documents: p.propertyFiles
                ?.filter((im: any) => im.fieldName === "docs")
              ?.map((f: any) => ({
                url: f?.fileName,
                description: f?.description,
                documentVisibility: f?.documentVisibility,
                showOnWebsite: f?.showOnWebsite,
              })),
            },
          };
        }),
        catchError((error) => {
          return of({});
        }),
      );
  }

  totalPropertiesFilters(data: any): Observable<any> {
    const lang = this._cookiesService.get("lang");

    return this.http
      .post<any>(
        `${environment.apiUrl}/properties/total-filters/${lang}`,
        data,
        { withCredentials: true },
      )
      .pipe(
        first(),
        catchError((error) => {
          return of({ total: 0 });
        }),
      );
  }

  /**
   * Get property by id
   * @param id
   * @returns
   */
  findOne(id: any): Observable<any> {
    const lang = this._cookiesService.get("lang");

    return this.http
      .get<any>(`${environment.apiUrl}/properties/${id}`, {
        withCredentials: true,
      })
      .pipe(
        first(),
        map((data) => {
          const propertySettingsStatus = data?.propertySettingsStatus?.find(
            (ps: any) => ps.propertySettings.order === 0,
          );

          const proposals = this.mapDataPropertyProposals(data);

          return {
          id: data?.id,
          details: this.mapDataDetails(data),
          isResale: data?.isResale === true,
          resaleOf: data?.resaleOf ?? null,
          minimumCommissionPendingValidation:
            data?.minimumCommissionPendingValidation === true,
            address: this.mapDataAddress(data),
            areasNearby: this.mapDataAreaNearby(data),
            Advertisement: this.mapDataAdvertisement(data),
            characteristics: this.mapDataCharacteristics(data),
            multimedia: {
              images: this.mapDataFiles(data, "images"),
              videos: this.mapDataVideos(data),
              first: data?.multimediaFirst ? data.multimediaFirst : 1,
            },
            propertyAgents: this.mapDataPropertyAgents(data),
            propertyCostumersContacts: this.mapDataPropertyCostumers(data),
            propertyOwnersContacts: this.mapDataPropertyOwners(data),
            portals: this.mapDataPropertyPortals(data),
            proposals: proposals,
            canChangeBusinessStatus: this.canChangeBusinessStatus(proposals),
            status: propertySettingsStatus ? true : false,
            updated: data?.updatedAt,
            documents: this.mapDataFiles(data, "docs"),
            mainAgent:
              data.propertyAgents.length > 0
                ? data.propertyAgents.find((pa: any) => pa.isMain === true)
                    ?.user.id
                : undefined,
            portalsLogs: data?.portalsLogs?.length,
            review: data.review,
            entity: data.entity,
            businessStatus: {
              name: data?.propertyBusinessStatus
                ?.propertyBusinessStatusTranslations?.[0]?.description,
              id: data?.propertyBusinessStatus?.id,
              code: data?.propertyBusinessStatus?.code,
            },
            reviewMessage: data?.reviewMessage,
            validatedAt: data?.validatedAt ?? null,
          };
        }),
        //catchError(error => { return of({}) })
      );
  }

  getDocumentDownloads(propertyId: string, take = 10, skip = 0) {
    return this.http
      .get<
        any[]
      >(`${environment.apiUrl}/properties/${propertyId}/document-downloads`, { withCredentials: true, params: { take, skip } })
      .pipe(first());
  }

  /**
   * Janela de recebimento de propostas efetiva deste imóvel. Numa fração o
   * valor é herdado do empreendimento a que pertence.
   */
  getProposalsWindow(id: any): Observable<any> {
    return this.http
      .get<any>(`${environment.apiUrl}/properties/${id}/proposals-window`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /**
   * Create property
   * @param data
   * @param data
   */
  createProperty(data: any): Observable<any> {
    return this.http
      .post<any>(`${environment.apiUrl}/properties`, data, {
        withCredentials: true,
      })
      .pipe(first());
  }

  duplicateProperty(id: any, reference?: string): Observable<any> {
    return this.http
      .post<any>(
        `${environment.apiUrl}/properties/${id}/duplicate`,
        { reference },
        { withCredentials: true },
      )
      .pipe(first());
  }

  /**
   * update property
   * @param id
   * @param data
   */
  updateProperty(id: any, data: any) {
    return this.http
      .patch<any>(`${environment.apiUrl}/properties/${id}`, data, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /**
   * Delete property
   * @param id
   */
  delete(id: string): Observable<any> {
    return this.http
      .delete<any>(`${environment.apiUrl}/properties/${id}`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /**
   * Delete properties by ids[]
   * @param ids
   * @returns
   */
  deleteByIds(ids: any) {
    return this.http
      .delete<any>(`${environment.apiUrl}/properties`, {
        withCredentials: true,
        body: { ids },
      })
      .pipe(first());
  }

  getDataForAllTab(): Observable<any> {
    const lang = this._cookiesService.get("lang");

    return this.http
      .get(`${environment.apiUrl}/get-form-properties/tab-all/${lang}`, {
        withCredentials: true,
      })
      .pipe(
        first(),
        map((data: any) => {
          const agents = this.mapDataListAgents(data.agents, false);
          return { ...data, agents };
        }),
      );
  }

  getDataForDetails(): Observable<any> {
    const lang = this._cookiesService.get("lang");
    return this.http
      .get(`${environment.apiUrl}/get-form-properties/details/${lang}`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  getDataForAddressCountries(): Observable<any> {
    const lang = this._cookiesService.get("lang");
    return this.http
      .get(
        `${environment.apiUrl}/get-form-properties/address/countries/${lang}`,
        { withCredentials: true },
      )
      .pipe(first());
  }

  getDataForAddressRegions(cid: any): Observable<any> {
    const lang = this._cookiesService.get("lang");
    return this.http
      .get(
        `${environment.apiUrl}/get-form-properties/address/regions/${cid}/${lang}`,
        { withCredentials: true },
      )
      .pipe(first());
  }

  getDataForAddressDistrict(rid: any): Observable<any> {
    const lang = this._cookiesService.get("lang");
    return this.http
      .get(
        `${environment.apiUrl}/get-form-properties/address/districts/${rid}/${lang}`,
        { withCredentials: true },
      )
      .pipe(first());
  }

  getDataForAddressCounties(did: any): Observable<any> {
    const lang = this._cookiesService.get("lang");
    return this.http
      .get(
        `${environment.apiUrl}/get-form-properties/address/counties/${did}/${lang}`,
        { withCredentials: true },
      )
      .pipe(first());
  }

  getDataForAddressParish(cid: any): Observable<any> {
    const lang = this._cookiesService.get("lang");
    return this.http
      .get(
        `${environment.apiUrl}/get-form-properties/address/parishes/${cid}/${lang}`,
        { withCredentials: true },
      )
      .pipe(first());
  }

  getDataForAddressAll(data: any): Observable<any> {
    const lang = this._cookiesService.get("lang");
    const params = new HttpParams({ fromObject: data });
    return this.http
      .get(`${environment.apiUrl}/get-form-properties/address/all/${lang}`, {
        params,
        withCredentials: true,
      })
      .pipe(first());
  }

  getDivisionsNearby() {
    const lang = this._cookiesService.get("lang");
    return this.http
      .get(
        `${environment.apiUrl}/get-form-properties/divisions-nearby/${lang}`,
        { withCredentials: true },
      )
      .pipe(first());
  }

  getCharacteristics() {
    const lang = this._cookiesService.get("lang");
    return this.http
      .get(
        `${environment.apiUrl}/get-form-properties/characteristics/${lang}`,
        { withCredentials: true },
      )
      .pipe(first());
  }

  getDataForAdvertisement() {
    const lang = this._cookiesService.get("lang");
    return this.http
      .get(`${environment.apiUrl}/get-form-properties/advertisement/${lang}`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  getDataForAgents(store: number, search = "", updateStoreId = false) {
    const lang = this._cookiesService.get("lang");

    let params = new HttpParams();

    if (search !== "") params = params.set("search", search);

    return this.http
      .get(`${environment.apiUrl}/get-form-properties/agents/store/${store}`, {
        params,
        withCredentials: true,
      })
      .pipe(
        first(),
        tap((data) => {
          this.propertyDataService.updateAgentList(
            this.mapDataListAgents(data, updateStoreId),
          );
        }),
        map((data) => this.mapDataListAgents(data, updateStoreId)),
      );
  }

  /***
   * Get All costumers
   */
  getAllCostumers(propertyId: any, page: number, search = "") {
    let params = new HttpParams().set("page", page);
    if (search !== "") params = params.set("search", search);
    return this.http
      .get<any>(
        `${environment.apiUrl}/contacts/property/${propertyId}/costumers`,
        { params, withCredentials: true },
      )
      .pipe(
        first(),
        catchError((error) => {
          return of({ costumers: [], total: 0 });
        }),
      );
  }

  /***
   * Get All owners
   */
  getAllOwners(propertyId: string, page: number, search = "") {
    let params = new HttpParams().set("page", page);

    if (search !== "") params = params.set("search", search);
    return this.http
      .get<any>(
        `${environment.apiUrl}/contacts/property/${propertyId}/owners`,
        { params, withCredentials: true },
      )
      .pipe(
        first(),
        catchError((error) => {
          return of({ owners: [], total: 0 });
        }),
      );
  }

  getDevelopmentsByStore(storeId: any) {
    const lang = this._cookiesService.get("lang");

    return this.http
      .get(`${environment.apiUrl}/properties/developments/store/${storeId}`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  getPropertiesByStoreAndPropertyType(storeId: any, propertyTypeId: any) {
    const lang = this._cookiesService.get("lang");

    return this.http
      .get(
        `${environment.apiUrl}/properties/store/${storeId}/property-type/${propertyTypeId}`,
        { withCredentials: true },
      )
      .pipe(first());
  }

  /** Checa unicidade de uma referência digitada */
  checkReference(ref: string) {
    let params = new HttpParams().set("ref", ref ?? "");

    return this.http.get<{ unique: boolean }>(
      `${environment.apiUrl}/properties/reference/check`,
      { params, withCredentials: true },
    );
  }

  /** Gera preview automático sem reservar seq */
  previewReference(
    isTemp: boolean,
    store?: any,
    agentId?: any,
    propertyType?: any,
  ) {
    let params = new HttpParams();

    if (store) params = params.set("store", String(store));
    if (agentId) params = params.set("agentId", String(agentId));
    if (propertyType) params = params.set("propertyType", String(propertyType));
    params = params.set("isTemp", String(isTemp));

    return this.http.get<{
      reference: string;
      missing: string[];
    }>(`${environment.apiUrl}/properties/reference/preview`, {
      params,
      withCredentials: true,
    });
  }

  previewAndSaveReference(
    propertyId: any,
    store?: any,
    agentId?: any,
    propertyType?: any,
  ) {
    let params = new HttpParams();

    if (store) params = params.set("store", String(store));
    if (agentId) params = params.set("agentId", String(agentId));
    if (propertyType) params = params.set("propertyType", String(propertyType));

    return this.http.patch<{
      reference: string;
    }>(
      `${environment.apiUrl}/properties/reference/preview-and-save/${propertyId}`,
      {},
      { params, withCredentials: true },
    );
  }

  getStoresByNetwork() {
    return this.http
      .get(`${environment.apiUrl}/get-form-properties/stores`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  getDistrictsByNetwork() {
    return this.http
      .get(`${environment.apiUrl}/get-form-properties/districts`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  getImage(url: string): Observable<any> {
    return this.http.get(url, { responseType: "blob", withCredentials: true });
    /*.pipe(
          switchMap(response => this.readFile(response))
        );*/
  }

  /** Master (imagem inteira, ≤ 2000 px) de uma imagem do imóvel, para o cropper. */
  getMasterImage(fileName: string): Observable<Blob> {
    return this.http.get(
      `${environment.apiUrl}/properties/file/original/${fileName}`,
      { responseType: "blob", withCredentials: true },
    );
  }

  updatePropertiesOrder(data: any) {
    return this.http.patch(
      `${environment.apiUrl}/properties/update-order`,
      data,
      { withCredentials: true },
    );
  }

  private readFile(blob: Blob): Observable<string> {
    return new Observable((obs: any) => {
      const reader = new FileReader();

      reader.onerror = (err) => obs.error(err);
      reader.onabort = (err) => obs.error(err);
      reader.onload = () => obs.next(reader.result);
      reader.onloadend = () => obs.complete();
      return reader.readAsDataURL(blob);
    });
  }

  mapDataDetails(p: any) {
    const lang = this._cookiesService.get("lang");
    const saleWithDeal = Array.isArray(p?.sale)
      ? (p.sale.find(
          (sale: any) =>
            sale?.status === "IN_PROGRESS" &&
            sale?.dealId !== null &&
            sale?.dealId !== undefined,
        ) ??
        p.sale.find(
          (sale: any) => sale?.dealId !== null && sale?.dealId !== undefined,
        ))
      : null;

    // Partilha interna: repõe o consultor vendedor a partir da comissão
    // SELLER da venda em curso atribuída à loja parceira.
    const inProgressSale = Array.isArray(p?.sale)
      ? p.sale.find((sale: any) => sale?.status === "IN_PROGRESS")
      : null;
    const internalShareSellerCommissionRow = p?.partnerStoreId
      ? inProgressSale?.commissions?.find(
          (c: any) =>
            c?.type === "SELLER" &&
            c?.status !== "CANCELED" &&
            c?.attributedStoreId === p?.partnerStoreId,
        )
      : null;

    const translationBusinessStatus =
      p?.propertyBusinessStatus?.propertyBusinessStatusTranslations?.find(
        (t: any) => t.language?.lang === lang,
      )?.description || p?.propertyBusinessStatus?.description;

    const translationBusinessType =
      p?.propertyBusinessType?.propertyBusinessTypeTranslations?.find(
        (t: any) => t.language?.lang === lang,
      )?.description || p?.propertyBusinessType?.description;

    return {
      reference: p?.reference,
      minimumCommissionPendingValidation:
        p?.minimumCommissionPendingValidation === true,
      minimumCommissionApproved:
        p?.minimumCommissionApprovedByMaster === true,
      store: p?.store.id,
      exclusive: p?.exclusive,
      propertyType: p?.propertyTypeId,
      propertyType2: p?.propertyTypeId2,
      condition: p?.propertyConditionId,
      yearOfConstruction: p?.yearOfConstruction,
      ec: p?.ecId,
      ecNumber: p?.ecNumber,
      ecExpirationDate: p?.ecExpirationDate,
      businessType: p?.propertyBusinessTypeId,
      businessTypeData: {
        description: translationBusinessType,
        id: p?.propertyBusinessType?.id,
      },
      businessStatus: p?.propertyBusinessStatusId,
      businessStatusData: {
        name: translationBusinessStatus,
        id: p?.propertyBusinessStatus?.id,
        code: p?.propertyBusinessStatus?.code,
      },
      price: p?.price,
      rentalPrice: p.rentalPrice,
      propertyValue: p.propertyValue,
      startDateMediation: p.startMediationAgreement,
      endDateMediation: p.endMediationAgreement,
      saleDate: p.saleDate,
      associatedProperty: p.associatedPropertyId,
      legislation: p.legislationId,
      useLicense: p.useLicense,
      buildingLicense: p.buildingLicense,
      dateLicense: p.dateLicense,
      technicalSheetLicense: p.technicalSheetLicense,
      hidePrice: p.hidePrice,
      hideExclusive: p.hideExclusive,
      hidePriceValue: p.hidePriceValue,
      hideECNumber: p.hideECNumber,
      hideECExpirationDate: p.hideECExpirationDate,
      hideUseLicense: p.hideUseLicense,
      hideDateLicense: p.hideDateLicense,
      hideTechnicalSheetLicense: p.hideTechnicalSheetLicense,
      hideBuildingLicense: p.hideBuildingLicense,
      inventoryValue: p?.inventoryValue,
      saleValue: p?.saleValue,
      saleCommission: p?.saleCommission,
      renewableEnergy: p?.renewableEnergy,
      carbonEmissions: p?.carbonEmissions,
      hideCarbonEmissions: p?.hideCarbonEmissions,
      hideRenewableEnergy: p?.hideRenewableEnergy,
      sharedSale: p?.sharedSale,
      sharedCommission: p?.sharedCommission,
      partnerEntityName: p?.partnerEntityName,
      shareType: p?.partnerStoreId ? "internal" : "external",
      partnerStoreId: p?.partnerStoreId ?? null,
      internalShareSellerId: internalShareSellerCommissionRow?.userId ?? null,
      internalShareSellerCommission:
        internalShareSellerCommissionRow?.percentage ?? null,
      isPercentageCommission: p?.isPercentageCommission,
      methodPaymentCommission: p?.methodPaymentCommission,
      splitCommissionValue: p?.splitCommissionValue,
      currency: p?.currencyId,
      contractNumber: p?.contractNumber,
      dealId: saleWithDeal?.dealId ?? null,
      paymentsConditions:
        p?.propertyPaymentConditions?.length > 0
          ? p?.propertyPaymentConditions
          : null,
      isTempReference: p?.isTempReference,
      keyNumber: p?.keyNumber,
      keyAddress: p?.keyAddress,
      hasPlate: p?.hasPlate,
      platePlacementDate: p?.platePlacementDate,
      plateRemovalDate: p?.plateRemovalDate,
      internalObservations: p?.internalObservations,
    };
  }

  mapDataAddress(p: any) {
    return {
      country: p?.propertyAddress[0]?.countryId,
      countryName: p?.propertyAddress[0]?.country.name,
      region: p?.propertyAddress[0]?.regionId,
      regionName: p?.propertyAddress[0]?.region.name,
      district: p?.propertyAddress[0]?.districtId,
      districtName: p?.propertyAddress[0]?.district.name,
      county: p?.propertyAddress[0]?.countyId,
      countyName: p?.propertyAddress[0]?.county.name,
      parish: p?.propertyAddress[0]?.parishId,
      parishName: p?.propertyAddress[0]?.parish.name,
      parishStatus: p?.propertyAddress[0]?.parish?.status,
      parishDeactivatedAt: p?.propertyAddress[0]?.parish?.deactivatedAt,
      propertyCreatedAt: p?.createdAt,
      address: p?.propertyAddress[0]?.address,
      zipCode: p?.propertyAddress[0]?.zipCode,
      zone: p?.propertyAddress[0]?.zone ?? "",
      doorNumber: p?.propertyAddress[0]?.door,
      fraction: p?.propertyAddress[0]?.fraction,
      floor: p?.propertyAddress[0]?.floor,
      longitude: p?.propertyAddress[0]?.longitude,
      latitude: p.propertyAddress[0]?.latitude,
      hidePropertyAddress: p?.hidePropertyAddress,
      hideMap: p?.hideMap,
      hideDistrict: p?.hideDistrict,
      hideCounty: p?.hideCounty,
      hideParish: p?.hideParish,
    };
  }

  mapDataAreaNearby(p: any) {
    const data: any = {};
    data.usefulArea = p?.usefulArea;
    data.grossArea = p?.grossArea;
    data.landArea = p?.landArea;
    data.grossDependentArea = p?.grossDependentArea;
    data.grossPrivateArea = p?.grossPrivateArea;
    data.buildingImplantationArea = p?.buildingImplantationArea;
    data.hideUsefulArea = p?.hideUsefulArea;
    data.hideGrossArea = p?.hideGrossArea;
    data.hideGrossDependentArea = p?.hideGrossDependentArea;
    data.hideGrossPrivateArea = p?.hideGrossPrivateArea;
    data.hideLandArea = p?.hideLandArea;
    data.hideBuildingImplantationArea = p?.hideBuildingImplantationArea;

    p?.propertyDivisions?.forEach((d: any) => {
      data[`division${d.divisionId}`] = d.val;

      d?.propertiesDivisionsList?.forEach((dpl: any) => {
        data[`areaInput-${d.divisionId}-${dpl.description}`] = dpl.unitVal;
      });
      data[`hideDivision${d.divisionId}`] = d.hide;
    });

    p?.propertyNearby?.forEach((n: any) => {
      data[`nearby${n.nearbyId}`] = n.nearbyId;
      data[`valNearby-${n.nearbyId}`] = n.unitVal;
      data[`unitNearBy-${n.nearbyId}`] = n.unitId;
      data[`hideNearby${n.nearbyId}`] = n.hide;
    });

    return data;
  }

  mapDataCharacteristics(p: any) {
    const data: any = {};
    p?.propertyCharacteristic?.forEach((c: any) => {
      const control = `p${c.characteristic.characteristicTypeId}_char${c.characteristicId}`;
      data[control] = c.characteristicId;
      // Quantidade (NUMBER) ou chave da opção (SELECT); o seletor valida.
      data[`${control}_value`] = c.valueText ?? c.value ?? null;
    });
    return data;
  }

  mapDataAdvertisement(p: any) {
    const data: any = {};
    p?.propertyTranslations?.forEach((p: any) => {
      data[`lang${p.languageId}_title`] = p.title;
      data[`lang${p.languageId}_description`] = p.description;
      data[`lang${p.languageId}_main`] = p.isMain;
    });

    p?.propertySettingsStatus?.forEach((p: any) => {
      data[`option${p.propertySettingsId}`] = p.propertySettingsId;
    });
    return data;
  }

  /**
   * Master + recorte guardado (recorte real, 2026-10). `hasMaster` falso =
   * imagem anterior: o cropper usa a publicada com zoom limitado.
   */
  mapImageCropFields(im: any) {
    const hasCrop =
      im?.cropX !== null && im?.cropX !== undefined && im?.cropW != null;
    return {
      hasMaster: !!im?.masterPath,
      masterWidth: im?.masterWidth ?? null,
      masterHeight: im?.masterHeight ?? null,
      crop: hasCrop
        ? {
            x: Number(im.cropX),
            y: Number(im.cropY),
            w: Number(im.cropW),
            h: Number(im.cropH),
            rotation: Number(im.cropRotation ?? 0),
            // Publicada inteira (laterais desfocadas ou brancas) em vez de recortada.
            fit: ["fit", "autofit", "fill"].includes(im?.cropMode),
            white: im?.cropMode === "fill",
          }
        : null,
      cropMode: im?.cropMode ?? null,
      cropDirty: false,
      cropPreview: null,
    };
  }

  mapDataFiles(p: any, type: any) {
    const dataFile = p?.propertyFiles?.filter(
      (im: any) => im.fieldName === type,
    );
    if (type === "images") {
      return dataFile?.map((im: any, index: any) => {
        return {
          id: im.order,
          name: im.originalName,
          img: `${environment.apiUrl}${environment.dirPropertiesImages}/${im.fileName}`,
          caption: im.description,
          thumb: `${environment.apiUrl}${environment.dirPropertiesImages}/${im.fileName}`,
          isNew: false,
          category: im.fieldName,
          fileName: im.fileName,
          fileId: im.id,
          autoCrop: false,
          originalFile: null,
          status: im.status,
          isAvailable: true,
          isAiGenerated: !!im.isAiGenerated,
          aiServiceCode: im.aiServiceCode ?? null,
          ...this.mapImageCropFields(im),
        };
      });
    } else if (type === "docs") {
      const dataDocs = dataFile?.map((im: any, index: any) => {
        return {
          id: im.id,
          name: im.originalName,
          path: `${environment.apiUrl}${environment.dirPropertiesDocs}/${im.fileName}`,
          isNew: false,
          type: im.mimeType,
          size: im.size,
          fileName: im.fileName,
          createdAt: im.createdAt,
          description: im.description,
          documentVisibility: im.documentVisibility ?? "PRIVATE",
          showOnWebsite: im.showOnWebsite ?? (im.description === "finishingMapPropertyDocument"),
          propertyDocumentTypeId: im.propertyDocumentTypeId ?? null,
          propertyDocumentTypeName: im.propertyDocumentType?.name ?? null,
          order: im.order,
        };
      });
      dataDocs.sort((a: any, b: any) => b.order - a.order);
      return dataDocs;
    } else {
      return [];
    }
  }

  mapDataVideos(p: any) {
    return p?.propertyVideos?.map((v: any, index: any) => {
      return {
        id: index + 1,
        link: this.sanitizer.bypassSecurityTrustResourceUrl(v.url),
        videoId: v.id,
        caption: "coption",
        isNew: false,
      };
    });
  }

  private toNumber(value: any): number | null {
    if (value === null || value === undefined || value === "") return null;

    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : null;
  }

  private findRangeByAmount(ranges: any[], amount: number) {
    if (!Array.isArray(ranges) || ranges.length === 0) return null;

    return (
      ranges.find((range) => {
        const minAmount = this.toNumber(range?.minAmount) ?? 0;
        const maxAmount = this.toNumber(range?.maxAmount);

        return (
          amount >= minAmount && (maxAmount === null || amount <= maxAmount)
        );
      }) ?? null
    );
  }

  private getRangePctByContract(
    range: any,
    contractType: string | null | undefined,
  ) {
    if (!range) return null;

    if (contractType === "SELF_EMPLOYED") {
      return range?.selfEmployedPct ?? null;
    }

    // Contrato de trabalho segue o escalao de empresa (ver getRangePercent no
    // commission-target.service do back-end).
    if (contractType === "COMPANY" || contractType === "EMPLOYMENT_CONTRACT") {
      return range?.companyPct ?? null;
    }

    return null;
  }

  private resolveCommissionTargetState(agentSource: any) {
    const userEntity = agentSource?.userEntity ?? agentSource?.user?.userEntity;
    const entity = userEntity?.entity ?? agentSource?.user?.userEntity?.entity;

    // verifica se a meta de comissão está ativa para o agente
    const commissionTargetEnabled = entity?.commissionTarget === true;

    // verifica se o plano de comissão da entidade está ativo
    const activePlan = entity?.entityCommissionPlans?.[0] ?? null;

    // pega a meta de comissão atual do agente caso haja
    const currentTarget =
      agentSource?.commissionTargets?.[0] ??
      agentSource?.user?.commissionTargets?.[0] ??
      null;
    const totalAmount = this.toNumber(currentTarget?.totalAmount) ?? 0;

    // busca o range que o agente se encontra para patamar inicial
    const currentRange =
      currentTarget?.currentRange ??
      this.findRangeByAmount(activePlan?.ranges ?? [], totalAmount);

    // verifica o tipo de contrato do agente
    const contractType = userEntity?.commissionContractType;

    // dependendo do tipo de contrato, busca a percentagem correspondente ao agente
    const currentPercent = this.getRangePctByContract(
      currentRange,
      contractType,
    );

    const targetManaged =
      commissionTargetEnabled && !!activePlan && currentPercent != null;

    return {
      targetManaged,
      currentPercent: targetManaged ? currentPercent : null,
    };
  }

  mapDataListAgents(data: any, updateStoreId: boolean) {
    if (updateStoreId) {
      this.propertyDataService.removeIdFilterAgent();

      let storeId =
        data?.length > 0 && data[0]?.usersStores[0]?.storeId
          ? data[0]?.usersStores[0]?.storeId
          : 0;

      this.propertyDataService.saveIdFilterAgent(storeId);
    }

    return data.map((a: any) => {
      const userImage = a.userFiles.length > 0 ? a.userFiles[0]?.fileName : "";
      const store = a?.usersStores[0]?.storeId;

      const commissionTargetState = this.resolveCommissionTargetState(a);

      return {
        ...a,
        agentFile: userImage,
        userFiles: undefined,
        add: false,
        totalProperties: a._count?.propertyAgents,
        totalSales: a.propertyAgents?.length,
        store: store,
        commissionTargetEnabled: commissionTargetState.targetManaged,
        commissionTargetCurrentPercent: commissionTargetState.currentPercent,
        // Comissão definida no utilizador (> 0) sobrepõe a % da meta.
        listingCommission: commissionTargetState.targetManaged
          ? Number(a?.userEntity?.listingCommission) > 0
            ? a?.userEntity?.listingCommission
            : commissionTargetState.currentPercent
          : a?.userEntity?.listingCommission,
        saleCommission: commissionTargetState.targetManaged
          ? Number(a?.userEntity?.saleCommission) > 0
            ? a?.userEntity?.saleCommission
            : commissionTargetState.currentPercent
          : a?.userEntity?.saleCommission,
      };
    });
  }

  mapDataPropertyAgents(data: any) {
    // this.propertyDataService.removeIdFilterAgent();
    // let storeId = data?.propertyAgents.length > 0 ? data.propertyAgents[0].user.usersStores[0].storeId : 0;
    // this.propertyDataService.saveIdFilterAgent(storeId);
    return data?.propertyAgents?.map((a: any) => {
      const userImage =
        a.user.userFiles.length > 0 ? a.user.userFiles[0]?.fileName : "";
      const store = a.user.usersStores[0].storeId;
      return {
        ...a.user,
        agentFile: userImage,
        userFiles: undefined,
        add: true,
        isMain: a.isMain,
        isListingAgent: a.isListingAgent,
        isSellingAgent: a.isSellingAgent,
        listingCommission:
          a?.listingCommission && a?.listingCommission != 0
            ? a?.listingCommission
            : a?.user?.userEntity?.listingCommission,
        saleCommission:
          a?.saleCommission && a?.saleCommission != 0
            ? a?.saleCommission
            : a?.user?.userEntity?.saleCommission,
        totalProperties: a.user?._count?.propertyAgents,
        totalSales: a.user?.propertyAgents?.length,
        store: store,
        usersStores: undefined,
      };
    });
  }

  mapDataPropertyCostumers(data: any) {
    const costumers = data?.costumers?.costumers?.map((c: any) => {
      return {
        id: c.id,
        firstName: c.firstName,
        lastName: c.lastName,
        fullName:
          c?.firstName && c?.lastName
            ? `${c.firstName} ${c.lastName}`
            : (c?.firstName ?? c?.lastName ?? null),
        email: c.email,
        status: c.status,
        contactType: c.contactType,
        phoneNumber: c.phoneNumber,
        phone: c.phone ?? c.phoneNumber ?? c.phoneNumber1 ?? c.phoneNumber2,
        address: c.address,
        city: c.city,
        zipCode: c.zipCode,
        postalCode: c.postalCode ?? c.zipCode,
        fiscalAddress: c.fiscalAddress,
        fiscalCity: c.fiscalCity,
        fiscalCountry: c.fiscalCountry,
        fiscalPostalCode: c.fiscalPostalCode,
        maritalStatus: c.maritalStatus,
        propertyRegime: c.propertyRegime,
        docType: c.docType,
        docNumber: c.docNumber,
        docExpiration: c.docExpiration,
        naturalness: c.naturalness,
        nationality: c.nationality,
        occupation: c.occupation,
        employer: c.employer,
        actionType: c.actionType,
        actionTypeOther: c.actionTypeOther,
        politicalPositions: c.politicalPositions,
        politicalDesc: c.politicalDesc,
        company: c.company,
        companyFiscalIdentification: c.companyFiscalIdentification,
        permanentRegistry: c.permanentRegistry,
        identificationDocument: c.docNumber,
        fiscalIdentification: c.fiscalIdentification,
        obs: c.obs,
        isSelected: false,
        isAddToProperty: true,
        isNew: false,
      };
    });
    return { costumers, total: data.costumers.total };
  }

  mapDataPropertyOwners(data: any) {
    const owners = data?.owners?.owners?.map((c: any) => {
      return {
        id: c.id,
        email: c.email,
        firstName: c.firstName,
        lastName: c.lastName,
        phoneNumber1: c.phoneNumber1,
        phoneNumber2: c.phoneNumber2,
        address: c.address,
        city: c.city,
        zipCode: c.zipCode,
        fiscalAddress: c.fiscalAddress,
        fiscalCity: c.fiscalCity,
        fiscalCountry: c.fiscalCountry,
        fiscalPostalCode: c.fiscalPostalCode,
        status: c.status,
        maritalStatus: c.maritalStatus,
        propertyRegime: c.propertyRegime,
        identificationDocument: c.docNumber,
        fiscalIdentification: c.fiscalIdentification,
        isSelected: false,
        isAddToProperty: true,
        isNew: false,
      };
    });
    return { owners, total: data.owners.total };
  }

  mapDataPropertyPortals(p: any) {
    const data: any = {};
    p?.propertyPortals?.forEach((portal: any) => {
      data[`portal${portal.portalId}`] = portal.status;
    });

    // data['externalPortals'] = p?.externalPortals;
    // data['portalsLogs'] = p?.portalsLogs;
    return {
      data,
      externalPortals: p?.externalPortals,
      portalsLogs: p?.portalsLogs,
    };
  }

  mapDataPropertyProposals(p: any) {
    if (p.proposals && p.proposals.length > 0) {
      const lang = this._cookiesService.get("lang");
      return p.proposals.map((proposal: any) => {
        return {
          ...proposal,
          history: proposal?.proposalsHistory?.map((h: any) => {
            return this.mapProposalHistory(h, lang);
          }),
          firstProposal: proposal?.proposalsHistory?.find(
            (f: any) => f.proposalStatus.description === "First Proposal",
          )
            ? this.mapProposalHistory(
                proposal?.proposalsHistory?.find(
                  (f: any) => f.proposalStatus.description === "First Proposal",
                ),
                lang,
              )
            : undefined,
          lastAction:
            proposal?.proposalsHistory?.length > 1
              ? this.mapProposalHistory(proposal?.proposalsHistory[0], lang)
              : undefined,
        };
      });
    } else {
      return [];
    }
  }

  mapProposalHistory(h: any, lang: any) {
    const status = h?.proposalStatus?.proposalStatusTranslation.find(
      (f: any) => f?.language?.lang === lang,
    );
    const documentPending =
      !!h?.requiresDocument &&
      !h?.documentDeliveredAt &&
      (!h?.proposalFile || h?.proposalFile.length === 0) &&
      !h?.expiredAt;

    return {
      id: h.id,
      proposalStatus: status?.description,
      proposalStatusCode: h?.proposalStatus?.description,
      proposalStatusId: h?.proposalStatusId,
      value: h?.value,
      obs: h?.obs,
      date: h?.createdAt,
      icon: h?.proposalStatus?.icon,
      file: h?.proposalFile,
      user: h?.createdBy,
      originalStatus: h?.proposalStatus?.description,
      saleDivision: h?.saleDivision,
      requiresDocument: h?.requiresDocument,
      useDeadline: h?.useDeadline,
      documentDeadline: h?.documentDeadline,
      documentRequestedAt: h?.documentRequestedAt,
      documentDeliveredAt: h?.documentDeliveredAt,
      expiredAt: h?.expiredAt,
      documentPending,
    };
  }

  canChangeBusinessStatus(proposals: any[]) {
    if (proposals && proposals?.length > 0) {
      const some = proposals.some((p) =>
        ["Accepted", "Sale Completed"].includes(p.lastAction?.originalStatus),
      );
      return some ? false : true;
    } else {
      return true;
    }
  }

  getAllReviews(propertyId: any, page: number, search = ""): Observable<any> {
    let params = new HttpParams().set("page", page);

    if (search !== "") params = params.set("search", search);

    return this.http.get<any>(
      `${environment.apiUrl}/properties/${propertyId}/reviews`,
      { params, withCredentials: true },
    );
  }

  getPropertyNotesAccess(
    propertyId: string,
  ): Observable<{ canRead: boolean; canWrite: boolean }> {
    return this.http
      .get<{ canRead: boolean; canWrite: boolean }>(
        `${environment.apiUrl}/properties/${propertyId}/notes/access`,
        { withCredentials: true },
      )
      .pipe(first());
  }

  getPropertyNotes(propertyId: string): Observable<any[]> {
    return this.http
      .get<any[]>(`${environment.apiUrl}/properties/${propertyId}/notes`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  createPropertyNote(propertyId: string, data: FormData): Observable<any> {
    return this.http
      .post<any>(`${environment.apiUrl}/properties/${propertyId}/notes`, data, {
        withCredentials: true,
      })
      .pipe(first());
  }

  refreshGeoreference(
    country: string,
    district: string,
    county: string,
    address: string,
    postalCode: string,
    parish: string,
  ) {
    let params = new HttpParams()
      .set("country", country)
      .set("district", district)
      .set("county", county)
      .set("format", "json");

    if (postalCode) params = params.set("postalcode", postalCode);
    if (address) params = params.set("street", address);
    if (parish) params = params.set("city", parish);

    return this.http.get<{ lat: string; lon: string }[]>(
      `https://nominatim.openstreetmap.org/search`,
      { params },
    );
  }

  refreshGeoreferenceByQuery(
    country: string,
    district: string,
    county: string,
    address: string,
  ) {
    let data = `${country},${district},${county}`;
    if (address) data += `,${address}`;

    let params = new HttpParams().set("q", data).set("format", "json");

    return this.http.get<{ lat: string; lon: string }[]>(
      `https://nominatim.openstreetmap.org/search`,
      { params },
    );
  }

  /**
   * Envia o imóvel para validação (Agent -> Store Operator/Master)
   * POST /properties/:id/validate
   */
  validateProperty(propertyId: string): Observable<any> {
    const lang = this._cookiesService.get("lang");

    return this.http
      .post(
        `${environment.apiUrl}/properties/${propertyId}/validate/${lang}`,
        {},
        { withCredentials: true },
      )
      .pipe(first());
  }

  /**
   * Aprova e ativa o imóvel (Store Operator/Master -> Agent)
   * POST /properties/:id/approve
   */
  approveProperty(propertyId: string): Observable<any> {
    const lang = this._cookiesService.get("lang");

    return this.http
      .post(
        `${environment.apiUrl}/properties/${propertyId}/approve/${lang}`,
        {},
        {
          withCredentials: true,
        },
      )
      .pipe(first());
  }

  /**
   * Recusa o imóvel (Store Operator/Master -> Agent)
   * POST /properties/:id/reject
   */
  rejectProperty(propertyId: string, message: string) {
    const lang = this._cookiesService.get("lang");

    return this.http
      .post<any>(
        `${environment.apiUrl}/properties/${propertyId}/reject/${lang}`,
        {
          message,
        },
        {
          withCredentials: true,
        },
      )
      .pipe(first());
  }

  createShowcaseSheet(body: any) {
    return this.http
      .post(
        `${environment.apiUrl}/showcase-sheet/create-showcase-sheet`,
        body,
        {
          withCredentials: true,
          responseType: "blob",
        },
      )
      .pipe(first());
  }

  getDataProperty(propertyId: any) {
    return this.http
      .get<any[]>(
        `${environment.apiUrl}/properties/${propertyId}/dataProperty`,
        {
          withCredentials: true,
        },
      )
      .pipe(first());
  }
}
